import React from "react";
import { motion } from "framer-motion";
import { useDashTheme } from "../hooks/useDashTheme";
import { resolveUserAvatar, resolveUserDisplayName } from "../../../utils/userAvatar";

// ── Card ────────────────────────────────────────────────────────────────────
// The base elevated surface. `interactive` adds a subtle hover lift.
export function Card({ children, interactive, padding = 16, style, onClick, ...rest }) {
  const t = useDashTheme();
  const Comp = interactive ? motion.div : "div";
  const motionProps = interactive
    ? { whileHover: { y: -2, boxShadow: t.shadow.md }, transition: t.motion.spring.snappy }
    : {};
  return (
    <Comp
      onClick={onClick}
      {...motionProps}
      style={{
        background: t.c.surface,
        border: `1px solid ${t.c.border}`,
        borderRadius: t.r.lg,
        padding,
        boxShadow: t.shadow.sm,
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
      {...rest}
    >
      {children}
    </Comp>
  );
}

// ── SectionCard ──────────────────────────────────────────────────────────────
// Card with a standard header (title, optional subtitle, right-aligned actions).
export function SectionCard({ title, subtitle, icon, accent, actions, children, bodyStyle, padding = 0, ...rest }) {
  const t = useDashTheme();
  // Named accent family (pastel fill, dark same-hue glyph) when the caller
  // gives one; neutral tile otherwise.
  const pair = accent ? t.accentPairs[accent] : null;
  return (
    <Card padding={0} {...rest}>
      {(title || actions) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: t.s[3],
            padding: `${t.s[3]}px ${t.s[4]}px`,
            borderBottom: `1px solid ${t.c.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: subtitle ? "flex-start" : "center", gap: t.s[2], minWidth: 0 }}>
            {icon && (
              <span
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 24, height: 24, borderRadius: t.r.sm, flexShrink: 0,
                  // Nudge so the tile sits on the title's cap-height, not between title+subtitle.
                  marginTop: subtitle ? 1 : 0,
                  background: pair ? pair.bg : t.c.surfaceMuted,
                  color: pair ? pair.fg : t.c.textSecondary,
                }}
              >
                {icon}
              </span>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: t.font.size.lg, fontWeight: t.font.weight.medium, color: t.c.text, lineHeight: 1.3 }}>
                {title}
              </div>
              {subtitle && (
                <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 2, lineHeight: 1.45 }}>{subtitle}</div>
              )}
            </div>
          </div>
          {actions && <div style={{ display: "flex", alignItems: "center", gap: t.s[2], flexShrink: 0 }}>{actions}</div>}
        </div>
      )}
      <div style={{ padding: padding || t.s[4], ...bodyStyle }}>{children}</div>
    </Card>
  );
}

// ── Button ────────────────────────────────────────────────────────────────────
export function Button({ variant = "secondary", size = "md", children, leftIcon, rightIcon, disabled, active, style, ...rest }) {
  const t = useDashTheme();
  const sizes = {
    sm: { h: 28, px: 10, fs: t.font.size.sm, gap: 6 },
    md: { h: 32, px: 12, fs: t.font.size.base, gap: 8 },
  }[size];

  const variants = {
    primary: { bg: t.c.primary, color: "#fff", border: "transparent", hoverBg: t.c.primaryHover },
    secondary: { bg: t.c.surface, color: t.c.text, border: t.c.borderStrong, hoverBg: t.c.surfaceMuted },
    ghost: { bg: "transparent", color: t.c.textSecondary, border: "transparent", hoverBg: t.c.surfaceMuted },
    danger: { bg: t.status.negative, color: "#fff", border: "transparent", hoverBg: t.status.negative },
  }[variant];

  return (
    <motion.button
      whileTap={disabled ? {} : { scale: 0.97 }}
      transition={t.motion.spring.snappy}
      disabled={disabled}
      style={{
        height: sizes.h, padding: `0 ${sizes.px}px`, fontSize: sizes.fs, gap: sizes.gap,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: active ? t.c.primarySoft : variants.bg,
        color: active ? t.c.primary : variants.color,
        border: `1px solid ${active ? "transparent" : variants.border}`,
        borderRadius: t.r.sm, fontWeight: t.font.weight.medium, fontFamily: t.font.family,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap", transition: "background 140ms ease, color 140ms ease",
        ...style,
      }}
      onMouseEnter={(e) => { if (!disabled && !active) e.currentTarget.style.background = variants.hoverBg; }}
      onMouseLeave={(e) => { if (!disabled && !active) e.currentTarget.style.background = variants.bg; }}
      {...rest}
    >
      {leftIcon}{children}{rightIcon}
    </motion.button>
  );
}

// ── TrendPill ──────────────────────────────────────────────────────────────────
// ▲ +12.4%  /  ▼ -3.1% vs previous period. Color-coded, accessible.
export function TrendPill({ value, suffix = "%", invert = false, size = "sm" }) {
  const t = useDashTheme();
  if (value == null || isNaN(value)) return null;
  const up = value > 0;
  const flat = value === 0;
  // invert=true → "down is good" (e.g. idle time)
  const good = invert ? !up : up;
  const color = flat ? t.status.neutral : good ? t.status.positive : t.status.negative;
  const soft = flat ? t.status.neutralSoft : good ? t.status.positiveSoft : t.status.negativeSoft;
  const arrow = flat ? "→" : up ? "↑" : "↓";
  const fs = size === "sm" ? t.font.size.xs : t.font.size.sm;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 3,
        padding: "1px 6px", borderRadius: t.r.xs, background: soft, color,
        fontSize: fs, fontWeight: t.font.weight.medium, lineHeight: 1.5,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <span style={{ fontSize: fs - 1 }}>{arrow}</span>
      {Math.abs(value).toFixed(1)}{suffix}
    </span>
  );
}

// ── Badge ──────────────────────────────────────────────────────────────────────
export function Badge({ tone = "neutral", children, dot }) {
  const t = useDashTheme();
  const map = {
    neutral: [t.status.neutral, t.status.neutralSoft],
    positive: [t.status.positive, t.status.positiveSoft],
    negative: [t.status.negative, t.status.negativeSoft],
    warning: [t.status.warning, t.status.warningSoft],
    info: [t.status.info, t.status.infoSoft],
    brand: [t.c.primary, t.c.primarySoft],
  };
  const [color, soft] = map[tone] || map.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "1px 7px",
      borderRadius: t.r.xs, background: soft, color, fontSize: t.font.size.xs,
      fontWeight: t.font.weight.medium, lineHeight: 1.6, whiteSpace: "nowrap",
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 99, background: color }} />}
      {children}
    </span>
  );
}

// ── Avatar ──────────────────────────────────────────────────────────────────────
// Prefer passing `user` so profile `avatar` resolves uniformly; `src` overrides.
export function Avatar({ name = "", size = 32, src, user, highlight }) {
  const t = useDashTheme();
  const resolvedSrc = src ?? resolveUserAvatar(user);
  const resolvedName = name || resolveUserDisplayName(user);
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [resolvedSrc]);
  const initials = resolvedName.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]?.toUpperCase()).join("");
  // deterministic named family from the name — pastel fill, dark same-hue glyph
  const pair = t.accentFor(resolvedName);
  return resolvedSrc && !broken ? (
    <img src={resolvedSrc} alt={resolvedName} onError={() => setBroken(true)} style={{ width: size, height: size, borderRadius: 99, objectFit: "cover", boxShadow: highlight ? `0 0 0 2px ${t.c.primary}` : "none" }} />
  ) : (
    <span style={{
      width: size, height: size, borderRadius: 99, display: "inline-flex",
      alignItems: "center", justifyContent: "center", flexShrink: 0,
      background: pair.bg, color: pair.fg, fontSize: size * 0.38,
      fontWeight: t.font.weight.semibold,
      boxShadow: highlight ? `0 0 0 2px ${t.c.primary}` : "none",
    }}>
      {initials || "?"}
    </span>
  );
}
